import { useEffect, useRef, useState } from "react";
import { Play, Pause, X, Loader2, Headphones, RotateCcw } from "lucide-react";
import api from "@/lib/api";
import { trackEvent } from "@/hooks/useArticleAnalytics";

const SPEEDS = [0.75, 1, 1.25, 1.5, 2];
const fmt = (s: number) => {
  if (!isFinite(s) || s < 0) return "0:00";
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

/* Sticky narration mini-player. Generation is server-side and durable —
   closing the page never cancels it; audio is cached per content version. */
export const NarrationPlayer = ({ slug, title, onClose }: {
  slug: string; title: string; onClose: () => void;
}) => {
  const [phase, setPhase] = useState<"loading" | "generating" | "ready" | "failed">("loading");
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speedIdx, setSpeedIdx] = useState(1);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  const handle = (d: any) => {
    if (d.status === "ready" && d.audio_url) {
      stopPoll();
      setAudioUrl(d.audio_url);
      if (d.duration_seconds) setDuration(d.duration_seconds);
      setPhase("ready");
    } else if (d.status === "failed") {
      stopPoll();
      setPhase("failed");
    } else {
      setPhase("generating");
    }
  };

  const start = async () => {
    setPhase("loading");
    try {
      const { data } = await api.post(`/articles/${slug}/narration`);
      handle(data);
      if (data.status !== "ready" && data.status !== "failed") {
        stopPoll();
        pollRef.current = setInterval(async () => {
          try { handle((await api.get(`/articles/${slug}/narration`)).data); } catch { /* keep polling */ }
        }, 4000);
      }
    } catch { setPhase("failed"); }
  };

  useEffect(() => {
    trackEvent("narration_open", { slug });
    start();
    return stopPoll;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    if (phase === "ready" && audioRef.current) {
      audioRef.current.playbackRate = SPEEDS[speedIdx];
      audioRef.current.play().then(() => setPlaying(true)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, audioUrl]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else { a.play().then(() => setPlaying(true)).catch(() => {}); trackEvent("narration_play", { slug }); }
  };

  const cycleSpeed = () => {
    const next = (speedIdx + 1) % SPEEDS.length;
    setSpeedIdx(next);
    if (audioRef.current) audioRef.current.playbackRate = SPEEDS[next];
  };

  const seek = (v: number) => {
    if (audioRef.current) { audioRef.current.currentTime = v; setTime(v); }
  };

  return (
    <div data-testid="narration-player" role="region" aria-label="Article narration player"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 w-[min(680px,calc(100vw-1.5rem))] rounded-2xl border border-white/[0.12] bg-[#0c0c13]/95 backdrop-blur-md shadow-[0_18px_50px_rgba(0,0,0,0.6)] px-4 py-3">
      {audioUrl && (
        <audio ref={audioRef} src={audioUrl} preload="metadata"
          onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
          onLoadedMetadata={(e) => { if (isFinite(e.currentTarget.duration)) setDuration(e.currentTarget.duration); }}
          onEnded={() => { setPlaying(false); trackEvent("narration_complete", { slug }); }} />
      )}
      {phase === "ready" ? (
        <div className="flex items-center gap-3">
          <button type="button" onClick={toggle} data-testid="narration-play-pause"
            aria-label={playing ? "Pause narration" : "Play narration"}
            className="h-10 w-10 shrink-0 rounded-full bg-[#7C3AED] hover:bg-[#8B5CF6] flex items-center justify-center text-white transition-colors">
            {playing ? <Pause className="h-4.5 w-4.5" /> : <Play className="h-4.5 w-4.5 ml-0.5" />}
          </button>
          <div className="min-w-0 flex-1">
            <p className="flex items-center gap-1.5 font-mono text-[8.5px] font-bold uppercase tracking-[0.24em] text-[#A78BFA]">
              <Headphones className="h-3 w-3" /> Listening · NewsLayer
            </p>
            <p className="text-[12px] font-semibold text-gray-200 truncate">{title}</p>
            <div className="mt-1 flex items-center gap-2">
              <span className="font-mono text-[9.5px] text-gray-500 w-8 text-right" data-testid="narration-elapsed">{fmt(time)}</span>
              <input type="range" min={0} max={duration || 0} step={1} value={Math.min(time, duration || 0)}
                onChange={(e) => seek(Number(e.target.value))} data-testid="narration-seek"
                aria-label="Seek narration"
                className="flex-1 h-1 accent-[#7C3AED] cursor-pointer" />
              <span className="font-mono text-[9.5px] text-gray-500 w-8" data-testid="narration-duration">{fmt(duration)}</span>
            </div>
          </div>
          <button type="button" onClick={cycleSpeed} data-testid="narration-speed"
            aria-label={`Playback speed ${SPEEDS[speedIdx]}x — tap to change`}
            className="shrink-0 rounded-lg border border-white/[0.12] px-2 py-1 font-mono text-[10.5px] font-bold text-gray-300 hover:text-white hover:border-white/25 transition-colors">
            {SPEEDS[speedIdx]}×
          </button>
          <button type="button" onClick={onClose} data-testid="narration-close" aria-label="Close narration player"
            className="shrink-0 text-gray-500 hover:text-white transition-colors p-1">
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : phase === "failed" ? (
        <div className="flex items-center gap-3" data-testid="narration-failed">
          <p className="text-[12.5px] text-gray-400 flex-1">Narration couldn't be prepared.</p>
          <button type="button" onClick={start} data-testid="narration-retry"
            className="inline-flex items-center gap-1.5 rounded-lg border border-[#7C3AED]/40 bg-[#7C3AED]/10 px-2.5 py-1.5 font-mono text-[10.5px] font-bold uppercase tracking-wider text-[#c4a6ff] hover:bg-[#7C3AED]/20 transition-colors">
            <RotateCcw className="h-3 w-3" /> Retry
          </button>
          <button type="button" onClick={onClose} aria-label="Close narration player"
            className="text-gray-500 hover:text-white transition-colors p-1">
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-3" data-testid="narration-generating">
          <span className="h-10 w-10 shrink-0 rounded-full border border-[#7C3AED]/40 bg-[#7C3AED]/10 flex items-center justify-center">
            <Loader2 className="h-4 w-4 text-[#A78BFA] animate-spin" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[12.5px] font-semibold text-gray-200">Preparing audio…</p>
            <p className="text-[11px] text-gray-500">About half a minute — it'll be instant for everyone after.</p>
          </div>
          <button type="button" onClick={onClose} data-testid="narration-close" aria-label="Close narration player"
            className="shrink-0 text-gray-500 hover:text-white transition-colors p-1">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};
